import type { Account } from '@interfaces/account';
import { AccountSearch } from '@ovComponents/3-pattern/accountSearch/accountSearch';
import { DraggableAccounts } from '@ovComponents/3-pattern/draggableAccounts/draggableAccounts';
import { Box, CircularProgress, Typography } from 'hephaestus/web';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
export type DraggableUngroupedAccountsProps = {
  ungroupedAccounts: Account[];
  setUngroupedAccounts: (value: Account[]) => void;
  variant: 'edit' | 'create';
  droppableId?: string;
  objectType?: 'USER' | 'HOUSEHOLD' | 'ORGANIZATION';
  objectId?: string;
  excludeAccountIds?: string[];
  includeAccounts?: Account[];
};

export const DraggableUngroupedAccounts = ({
  objectType,
  objectId,
  ungroupedAccounts,
  setUngroupedAccounts,
  variant,
  droppableId = 'ungroupedAccounts',
  excludeAccountIds = [],
  includeAccounts = [],
}: DraggableUngroupedAccountsProps) => {
  const { t } = useTranslation('client');
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [searchText, setSearchText] = useState('');
  const [initialLoading, setInitialLoading] = useState(false);
  const loadMoreRef = useRef<(() => void) | null>(null);
  const searchTextRef = useRef(searchText);

  const handleSearchText = (value: string) => {
    searchTextRef.current = value;
    setSearchText(value);
  };

  return (
    <>
      <Typography
        variant='bodyMedium'
        sx={{ fontWeight: 500, mt: 3, mb: 2 }}
        color='textSecondary'
      >
        {t('accountsGroup.ungroupedAccounts')}
      </Typography>
      <AccountSearch
        label=''
        placeholder={t('accountsGroup.searchUngroupedAccounts')}
        setAccounts={(accounts) =>
          setUngroupedAccounts(
            searchTextRef.current.length === 0 && includeAccounts.length > 0
              ? [...includeAccounts, ...accounts]
              : accounts,
          )
        }
        setHasMore={setHasMore}
        setLoadMoreHandler={(fn) => {
          loadMoreRef.current = fn;
        }}
        objectType={objectType}
        objectId={objectId}
        isUngrouped
        excludeAccountIds={excludeAccountIds}
        sx={{ mb: 2 }}
        page={page}
        setPage={setPage}
        searchText={searchText}
        setSearchText={handleSearchText}
        setLoading={setLoading}
        setInitialLoading={setInitialLoading}
      />
      {initialLoading ? (
        <Box
          sx={{
            height: '30vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <DraggableAccounts
          droppableId={droppableId}
          accounts={ungroupedAccounts}
          variant={variant}
          hasMore={hasMore}
          handleLoadMore={() => loadMoreRef.current?.()}
          loading={loading}
          objectType={objectType}
        />
      )}
    </>
  );
};
